/** Business country → default currency, locale, and time zone (ISO 3166-1 / 4217). */

export type Territory = {
  code: string;
  name: string;
  currency: string;
  locale: string;
  timeZone: string;
};

/** Markets cevop actually ships for right now. Add countries when we support them. */
export const BUSINESS_TERRITORIES: readonly Territory[] = [
  { code: 'NG', name: 'Nigeria', currency: 'NGN', locale: 'en-NG', timeZone: 'Africa/Lagos' },
  { code: 'GH', name: 'Ghana', currency: 'GHS', locale: 'en-GH', timeZone: 'Africa/Accra' },
] as const;

const BY_CODE = new Map(BUSINESS_TERRITORIES.map((row) => [row.code, row]));

export const DEFAULT_TERRITORY: Territory = BUSINESS_TERRITORIES[0]!;

export function territoryForCountry(code: string | undefined): Territory {
  if (!code) return DEFAULT_TERRITORY;
  return BY_CODE.get(code.toUpperCase()) ?? DEFAULT_TERRITORY;
}

export function localeForCurrency(currency: string, locale?: string): string {
  if (locale) return locale;
  const match = BUSINESS_TERRITORIES.find((row) => row.currency === currency);
  return match?.locale ?? DEFAULT_TERRITORY.locale;
}

export function uniqueCurrencies(): string[] {
  return [...new Set(BUSINESS_TERRITORIES.map((row) => row.currency))].sort();
}

/**
 * Guess country from device timezone / language — not GPS.
 * Wrong guesses (VPN, travel) stay overridable in onboarding and Settings.
 */
export function inferTerritory(hints?: { timeZone?: string; languages?: readonly string[] }): Territory {
  const timeZone =
    hints?.timeZone ??
    (typeof Intl !== 'undefined' ? Intl.DateTimeFormat().resolvedOptions().timeZone : undefined);
  if (timeZone) {
    const byZone = BUSINESS_TERRITORIES.find((row) => row.timeZone === timeZone);
    if (byZone) return byZone;
  }

  const languages =
    hints?.languages ??
    (typeof navigator !== 'undefined'
      ? [navigator.language, ...(navigator.languages ?? [])]
      : []);
  for (const language of languages) {
    const region = language.split(/[-_]/)[1]?.toUpperCase();
    if (!region) continue;
    const match = BY_CODE.get(region);
    if (match) return match;
  }

  return DEFAULT_TERRITORY;
}
